/**
 * Everything the app reads and writes about accounts (Phase 1, step 1.1).
 *
 * `accounts` is one of the few tables the client writes directly — the baseline grants
 * SELECT/INSERT/UPDATE and no DELETE, so an account is retired by archiving it and the
 * ledger keeps every reference it ever had. The database also owns the two rules that
 * matter: the mirrored ledger account (trigger) and the refusal to archive an account that
 * still holds money (`ACCOUNT_NOT_EMPTY`).
 */
import {
  buildOpeningEntry,
  ledgerKindOfAccountType,
  type AccountType,
  type CaptureMode,
  type UsageScope,
} from '@pfo/domain';
import type { Json } from '@pfo/database';

import { supabase } from '../../../shared/api/supabase/client.js';
import { instantForDate } from '../../../shared/lib/dates.js';
import { appErrorException } from '../../../shared/lib/errors.js';
import { fetchPreferences } from '../../preferences/data/preferencesRepository.js';

export type Account = Readonly<{
  id: string;
  name: string;
  accountType: AccountType;
  institution: string | null;
  usageScope: UsageScope;
  captureMode: CaptureMode;
  isActive: boolean;
  sortOrder: number;
  /** Straight from the SQL view — the client never sums money (DBD-014). */
  balance: number;
  /**
   * Whether anything was ever posted to this account. A liability at zero means "paid off"
   * only if it once held a debt — a freshly created one is at zero too, and telling the user
   * to close it the moment they made it would be nonsense (B5).
   */
  hasMovement: boolean;
  isDefault: boolean;
}>;

export type AccountsOverview = Readonly<{
  accounts: readonly Account[];
  defaultAccountId: string | null;
}>;

export type NewAccount = Readonly<{
  name: string;
  accountType: AccountType;
  usageScope: UsageScope;
  captureMode: CaptureMode;
  institution?: string;
  /** Amount held, or owed on a liability. 0 or undefined posts no opening entry. */
  openingBalance?: number;
  /** Date the balance was true (YYYY-MM-DD). Defaults to today. */
  openingDate?: string;
}>;

export type AccountContext = Readonly<{
  householdId: string;
  subjectId: string;
}>;

/** Statement row as the SQL view computed it, one per month with movement. */
export type StatementMonth = Readonly<{
  periodStart: string;
  openingBalance: number;
  /** Debit side: money into an asset, money paid off a liability. */
  debitTotal: number;
  /** Credit side: money out of an asset, new debt on a liability. */
  creditTotal: number;
  closingBalance: number;
}>;

export async function fetchAccountsOverview(): Promise<AccountsOverview> {
  const [accountsResult, balancesResult, movementResult, preferences] = await Promise.all([
    supabase
      .from('accounts')
      .select(
        'id, name, account_type, institution, usage_scope, capture_mode, is_active, sort_order',
      )
      .order('sort_order')
      .order('name'),
    supabase.from('account_balances').select('account_id, current_balance'),
    // The rollup holds one row per account and month with movement (migration 0005), so this
    // answers "was this account ever used" without touching a journal line.
    supabase.from('account_period_totals').select('account_id'),
    fetchPreferences(),
  ]);

  if (accountsResult.error) throw appErrorException(accountsResult.error);
  if (balancesResult.error) throw appErrorException(balancesResult.error);
  if (movementResult.error) throw appErrorException(movementResult.error);

  const balanceByAccountId = new Map(
    (balancesResult.data ?? []).map((row) => [row.account_id, Number(row.current_balance ?? 0)]),
  );
  const accountsWithMovement = new Set((movementResult.data ?? []).map((row) => row.account_id));
  const { defaultAccountId } = preferences;

  return {
    accounts: (accountsResult.data ?? []).map((row) => ({
      id: row.id,
      name: row.name,
      accountType: row.account_type as AccountType,
      institution: row.institution,
      usageScope: row.usage_scope as UsageScope,
      captureMode: row.capture_mode as CaptureMode,
      isActive: row.is_active,
      sortOrder: row.sort_order,
      balance: balanceByAccountId.get(row.id) ?? 0,
      hasMovement: accountsWithMovement.has(row.id),
      isDefault: row.id === defaultAccountId,
    })),
    defaultAccountId,
  };
}

export async function fetchAccount(accountId: string): Promise<Account | null> {
  const overview = await fetchAccountsOverview();
  return overview.accounts.find((account) => account.id === accountId) ?? null;
}

export async function fetchAccountStatement(accountId: string): Promise<readonly StatementMonth[]> {
  const { data, error } = await supabase
    .from('v_account_monthly_statement')
    .select('period_start, debit_total, credit_total, opening_balance, closing_balance')
    .eq('account_id', accountId)
    .order('period_start', { ascending: false });
  if (error) throw appErrorException(error);

  return (data ?? []).map((row) => ({
    periodStart: row.period_start ?? '',
    openingBalance: Number(row.opening_balance ?? 0),
    debitTotal: Number(row.debit_total ?? 0),
    creditTotal: Number(row.credit_total ?? 0),
    closingBalance: Number(row.closing_balance ?? 0),
  }));
}

/**
 * Creates the account, then posts its opening balance as a journal entry (DBD-006 — there
 * is no balance column to set).
 *
 * These are two calls on purpose (plan step 1.1): the row first, then the entry the trigger
 * made possible by mirroring it into a ledger account. If the entry fails, the account
 * exists without a starting balance and the account screen offers to record it — the
 * partial state is visible and fixable rather than hidden behind a rollback the client
 * cannot perform.
 */
export async function createAccount(
  context: AccountContext,
  input: NewAccount,
): Promise<{ accountId: string; openingBalanceFailed: boolean }> {
  const { data, error } = await supabase
    .from('accounts')
    .insert({
      household_id: context.householdId,
      owner_subject_id: context.subjectId,
      name: input.name.trim(),
      account_type: input.accountType,
      usage_scope: input.usageScope,
      capture_mode: input.captureMode,
      ...(input.institution && input.institution.trim() !== ''
        ? { institution: input.institution.trim() }
        : {}),
    })
    .select('id')
    .single();
  if (error) throw appErrorException(error);

  const accountId = data.id;
  const opening = input.openingBalance ?? 0;
  if (opening === 0) {
    return { accountId, openingBalanceFailed: false };
  }

  try {
    await postOpeningBalance(context, {
      accountId,
      accountType: input.accountType,
      amount: opening,
      ...(input.openingDate === undefined ? {} : { openingDate: input.openingDate }),
    });
  } catch {
    return { accountId, openingBalanceFailed: true };
  }

  return { accountId, openingBalanceFailed: false };
}

export type OpeningBalanceInput = Readonly<{
  accountId: string;
  accountType: AccountType;
  /** Positive integer VND: amount held, or amount owed on a liability. */
  amount: number;
  openingDate?: string;
}>;

/** Also the repair path when the entry failed the first time (see `createAccount`). */
export async function postOpeningBalance(
  context: AccountContext,
  input: OpeningBalanceInput,
): Promise<void> {
  const [ledgerAccountId, openingBalancesId] = await Promise.all([
    ledgerAccountIdOfAccount(input.accountId),
    systemLedgerAccountId(context.householdId, 'Opening Balances'),
  ]);

  const payload = buildOpeningEntry({
    subjectId: context.subjectId,
    ledgerAccountId,
    ledgerKind: ledgerKindOfAccountType(input.accountType),
    openingBalancesLedgerAccountId: openingBalancesId,
    amount: input.amount,
    occurredAt: openingInstant(input.openingDate),
    description: 'Số dư đầu kỳ',
  });

  const { error } = await supabase
    .schema('app')
    .rpc('post_entry', { p: payload as unknown as Json });
  if (error) throw appErrorException(error);
}

/** The one `opening` entry an account has, or null (DBD-006: at most one, recorded once). */
export type OpeningEntry = Readonly<{ entryId: string; occurredAt: string; amount: number }>;

export async function fetchOpeningEntry(
  accountId: string,
  accountType: AccountType,
): Promise<OpeningEntry | null> {
  const ledgerAccountId = await ledgerAccountIdOfAccount(accountId);
  const { data, error } = await supabase
    .from('journal_entries')
    .select('id, occurred_at, journal_lines!inner(ledger_account_id, debit_amount, credit_amount)')
    .eq('entry_type', 'opening')
    .is('deleted_at', null)
    .eq('journal_lines.ledger_account_id', ledgerAccountId)
    .order('occurred_at', { ascending: true })
    .limit(1);
  if (error) throw appErrorException(error);
  const row = data?.[0];
  if (row === undefined) return null;
  const line = row.journal_lines[0];
  const isAsset = ledgerKindOfAccountType(accountType) === 'asset';
  return {
    entryId: row.id,
    occurredAt: row.occurred_at,
    amount: Number((isAsset ? line?.debit_amount : line?.credit_amount) ?? 0),
  };
}

/**
 * Corrects the opening balance that was typed wrong when the account was created (decision
 * AJ1): the same `opening` entry is rewritten in place through `update_entry` (A7), on its
 * original date, so the account's history starts where it always did — only the number moves.
 * A closed month refuses with MONTH_CLOSED, like every other correction.
 */
export async function updateOpeningBalance(
  context: AccountContext,
  input: OpeningBalanceInput & { entryId: string; occurredAt: string },
): Promise<void> {
  const [ledgerAccountId, openingBalancesId] = await Promise.all([
    ledgerAccountIdOfAccount(input.accountId),
    systemLedgerAccountId(context.householdId, 'Opening Balances'),
  ]);
  const payload = buildOpeningEntry({
    subjectId: context.subjectId,
    ledgerAccountId,
    ledgerKind: ledgerKindOfAccountType(input.accountType),
    openingBalancesLedgerAccountId: openingBalancesId,
    amount: input.amount,
    occurredAt: input.occurredAt,
    description: 'Số dư đầu kỳ',
  });
  const { error } = await supabase
    .schema('app')
    .rpc('update_entry', { p_entry_id: input.entryId, p: payload as unknown as Json });
  if (error) throw appErrorException(error);
}

export type UpdateAccountInput = Readonly<{
  name?: string;
  captureMode?: CaptureMode;
}>;

export async function updateAccount(accountId: string, input: UpdateAccountInput): Promise<void> {
  const patch: { name?: string; capture_mode?: CaptureMode } = {};
  if (input.name !== undefined) {
    patch.name = input.name.trim();
  }
  if (input.captureMode !== undefined) {
    patch.capture_mode = input.captureMode;
  }
  const { error } = await supabase.from('accounts').update(patch).eq('id', accountId);
  if (error) throw appErrorException(error);
}

export async function renameAccount(accountId: string, name: string): Promise<void> {
  return updateAccount(accountId, { name });
}

/**
 * Archive (`isActive: false`) or bring back an account. The database refuses to archive one
 * that still holds money — the error surfaces as a message telling the user what to do
 * (`ACCOUNT_NOT_EMPTY`).
 */
export async function setAccountActive(accountId: string, isActive: boolean): Promise<void> {
  const { error } = await supabase
    .from('accounts')
    .update({ is_active: isActive })
    .eq('id', accountId);
  if (error) throw appErrorException(error);
}

async function ledgerAccountIdOfAccount(accountId: string): Promise<string> {
  const { data, error } = await supabase
    .from('ledger_accounts')
    .select('id')
    .eq('linked_account_id', accountId)
    .single();
  if (error) throw appErrorException(error);
  return data.id;
}

async function systemLedgerAccountId(householdId: string, name: string): Promise<string> {
  const { data, error } = await supabase
    .from('ledger_accounts')
    .select('id')
    .eq('household_id', householdId)
    .eq('is_system', true)
    .eq('name', name)
    .single();
  if (error) throw appErrorException(error);
  return data.id;
}

/** An opening balance is true as of a date, but the ledger stores instants. */
function openingInstant(openingDate: string | undefined): string {
  return openingDate === undefined || openingDate === ''
    ? new Date().toISOString()
    : instantForDate(openingDate);
}
